import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Smile, Frown, Meh, Heart, Brain, Plus, ChevronRight } from 'lucide-react';
import { usePeriodStore } from '../../store/periodStore';

const moods = [
  { id: 'happy', name: 'Happy', icon: <Smile className="w-4 h-4" />, color: 'text-success' },
  { id: 'sad', name: 'Sad', icon: <Frown className="w-4 h-4" />, color: 'text-destructive' },
  { id: 'neutral', name: 'Neutral', icon: <Meh className="w-4 h-4" />, color: 'text-warning' },
  { id: 'emotional', name: 'Emotional', icon: <Heart className="w-4 h-4" />, color: 'text-pink-500' },
  { id: 'anxious', name: 'Anxious', icon: <Brain className="w-4 h-4" />, color: 'text-purple-500' }
];

export function MoodTracker() {
  const { addMood, getMoodByDate } = usePeriodStore();
  const [selectedMood, setSelectedMood] = useState<string | null>(null);
  const [note, setNote] = useState('');

  const handleAddMood = () => {
    if (!selectedMood) return;

    addMood({
      type: selectedMood,
      note,
      date: new Date().toISOString()
    });

    setSelectedMood(null);
    setNote('');
  };

  const todaysMood = getMoodByDate(new Date().toISOString().split('T')[0]);

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold">Log Mood</h2>

      {/* Mood Selection */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {moods.map((mood) => (
          <motion.button
            key={mood.id}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setSelectedMood(mood.id)}
            className={`theme-card p-4 relative overflow-hidden group ${
              selectedMood === mood.id ? 'ring-2 ring-primary' : ''
            }`}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="relative flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`relative ${mood.color}`}>
                  {mood.icon}
                  <div className="absolute inset-0 bg-current opacity-20 blur-xl rounded-full" />
                </div>
                <span className="font-medium">{mood.name}</span>
              </div>
              <ChevronRight className="w-4 h-4 text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          </motion.button>
        ))}
      </div>

      {/* Notes Input */}
      {selectedMood && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-4"
        >
          <div className="space-y-2">
            <label className="text-sm font-medium">Notes (optional)</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="w-full h-24 rounded-lg bg-muted p-3 text-sm resize-none"
              placeholder="How are you feeling today?"
            />
          </div>

          <button
            onClick={handleAddMood}
            className="w-full theme-button py-3 rounded-lg flex items-center justify-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Save Mood
          </button>
        </motion.div>
      )}

      {/* Today's Mood */}
      {todaysMood && (
        <div className="space-y-3">
          <h3 className="text-sm font-medium">Today's Mood</h3>
          <div className="bg-muted/30 p-4 rounded-lg space-y-2">
            <div className="flex items-center gap-2">
              {moods.find(m => m.id === todaysMood.type)?.icon}
              <span className="font-medium">
                {moods.find(m => m.id === todaysMood.type)?.name}
              </span>
            </div>
            {todaysMood.note && (
              <p className="text-sm text-muted-foreground">{todaysMood.note}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}